import { GetAllCompanyDto } from "@/types/api/api-types";
import { PreferenceLine } from "../preferenceLine";
import { formatDate } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditBillingEntityFieldModal } from "./editBillingEntityFieldModal";
import { userAtom } from "@/stores/authStore";
import { useAtomValue } from "jotai";
import { Button } from "@/components/ui/button";
import { Pen } from "lucide-react";
import { RolesEnum } from "@/constants/enums/roles.enum";

type GeneralInfoCompanyProps = {
  company?: GetAllCompanyDto;
};

type EditableField =
  | "midCode"
  | "internalName"
  | "companyName"
  | "street"
  | "postalCode"
  | "city"
  | "numberTVA"
  | "companySiret"
  | "companyEori"
  | "email"
  | "emailBis";

type EditingState = {
  billingEntityId: string;
  field: EditableField;
};

type FieldConfig = { title: string; label: string; placeholder: string };

const useFieldConfig = (): Record<EditableField, FieldConfig> => {
  const { t } = useTranslation("boClients");
  return useMemo(
    () => ({
      midCode: {
        title: t("billingEntityEditModal.midCodeTitle"),
        label: t("billingEntityEditModal.midCodeLabel"),
        placeholder: t("billingEntityEditModal.midCodePlaceholder"),
      },
      internalName: {
        title: t("billingEntityEditModal.internalNameTitle"),
        label: t("billingEntityEditModal.internalNameLabel"),
        placeholder: t("billingEntityEditModal.internalNamePlaceholder"),
      },
      companyName: {
        title: t("billingEntityEditModal.companyNameTitle"),
        label: t("billingEntityEditModal.companyNameLabel"),
        placeholder: t("billingEntityEditModal.companyNamePlaceholder"),
      },
      street: {
        title: t("billingEntityEditModal.streetTitle"),
        label: t("billingEntityEditModal.streetLabel"),
        placeholder: t("billingEntityEditModal.streetPlaceholder"),
      },
      postalCode: {
        title: t("billingEntityEditModal.postalCodeTitle"),
        label: t("billingEntityEditModal.postalCodeLabel"),
        placeholder: t("billingEntityEditModal.postalCodePlaceholder"),
      },
      city: {
        title: t("billingEntityEditModal.cityTitle"),
        label: t("billingEntityEditModal.cityLabel"),
        placeholder: t("billingEntityEditModal.cityPlaceholder"),
      },
      numberTVA: {
        title: t("billingEntityEditModal.numberTVATitle"),
        label: t("billingEntityEditModal.numberTVALabel"),
        placeholder: t("billingEntityEditModal.numberTVAPlaceholder"),
      },
      companySiret: {
        title: t("billingEntityEditModal.companySiretTitle"),
        label: t("billingEntityEditModal.companySiretLabel"),
        placeholder: t("billingEntityEditModal.companySiretPlaceholder"),
      },
      companyEori: {
        title: t("billingEntityEditModal.companyEoriTitle"),
        label: t("billingEntityEditModal.companyEoriLabel"),
        placeholder: t("billingEntityEditModal.companyEoriPlaceholder"),
      },
      email: {
        title: t("billingEntityEditModal.emailTitle"),
        label: t("billingEntityEditModal.emailLabel"),
        placeholder: t("billingEntityEditModal.emailPlaceholder"),
      },
      emailBis: {
        title: t("billingEntityEditModal.emailBisTitle"),
        label: t("billingEntityEditModal.emailBisLabel"),
        placeholder: t("billingEntityEditModal.emailBisPlaceholder"),
      },
    }),
    [t],
  );
};

export const GeneralInfoCompany = ({ company }: GeneralInfoCompanyProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const fieldConfig = useFieldConfig();
  const user = useAtomValue(userAtom);
  const [editing, setEditing] = useState<EditingState | null>(null);

  if (!company)
    return (
      <div className="flex w-full flex-1 flex-col gap-6 lg:px-8">
        <div className="flex flex-row justify-between gap-2 border-0 border-b border-gray-200 pb-5">
          <div className="items-left flex flex-col gap-2">
            <h2 className="text-lg font-semibold">{t("clientCompany.title")}</h2>
            <p className="text-sm text-gray-600">{t("clientCompany.subtitle")}</p>
          </div>
        </div>
        <div>{t("common:states.loading")}</div>
      </div>
    );

  const editingBillingEntity = editing
    ? company.billingEntities.find((e) => e.id === editing.billingEntityId)
    : undefined;

  const defaultBillingEntity = company.billingEntities.find((el) => el.isDefault);

  const canEdit = user?.role === RolesEnum.SUPERADMIN || user?.role === RolesEnum.ACCOUNTANT;

  const buildEditHandler = (billingEntityId: string) =>
    canEdit ? (field: EditableField) => setEditing({ billingEntityId, field }) : undefined;

  return (
    <div className="flex w-full flex-1 flex-col gap-6 lg:px-8">
      <div className="flex flex-row justify-between gap-2 border-0 border-b border-gray-200 pb-5">
        <div className="items-left flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{t("clientCompany.title")}</h2>
          <p className="text-sm text-gray-600">{t("clientCompany.subtitle")}</p>
        </div>
      </div>
      <div className="flex flex-col gap-10">
        {defaultBillingEntity && (
          <div className="flex flex-col">
            <h3 className="text-base font-medium text-black">
              {t("clientCompany.defaultBillingEntity")}
            </h3>
            <BillingEntityGroup
              {...defaultBillingEntity}
              onEdit={buildEditHandler(defaultBillingEntity.id)}
            />
          </div>
        )}
        {company.billingEntities
          .filter((el) => !el.isDefault)
          .map((billingEntity) => (
            <div className="flex flex-col" key={billingEntity.id}>
              <h3 className="text-base font-medium text-black">
                {billingEntity.deletedAt
                  ? t("clientCompany.deletedAdditionalBillingEntity", {
                      date: formatDate(billingEntity.deletedAt, "dd/MM/yyyy"),
                    })
                  : t("clientCompany.additionalBillingEntity")}
              </h3>
              <BillingEntityGroup {...billingEntity} onEdit={buildEditHandler(billingEntity.id)} />
            </div>
          ))}
      </div>
      {editing && (
        <EditBillingEntityFieldModal
          open={Boolean(editingBillingEntity)}
          onClose={() => setEditing(null)}
          billingEntity={editingBillingEntity}
          field={editing.field}
          {...fieldConfig[editing.field]}
        />
      )}
    </div>
  );
};

type BillingEntityGroupProps = GetAllCompanyDto["billingEntities"][0] & {
  onEdit?: (field: EditableField) => void;
};

const EditButton = ({
  onEdit,
  field,
}: {
  onEdit?: (f: EditableField) => void;
  field: EditableField;
}) =>
  onEdit && (
    <Button type="button" variant="ghost" onClick={() => onEdit(field)} className="px-1 py-0">
      <Pen className="text-gray-500 hover:text-black" />
    </Button>
  );

const BillingEntityGroup = ({
  companyName,
  internalName,
  street,
  postalCode,
  city,
  country,
  numberTVA,
  companySiret,
  companyEori,
  email,
  emailBis,
  midCode,
  onEdit,
}: BillingEntityGroupProps) => {
  const { t } = useTranslation("boClients");
  const undefinedValue = t("clientCompany.undefinedValue");
  return (
    <div className="flex flex-col gap-0">
      <PreferenceLine
        title={t("clientCompany.companyNameLabel")}
        value={[companyName]}
        titleEnd={<EditButton onEdit={onEdit} field="companyName" />}
      />
      <PreferenceLine
        title={t("clientCompany.internalNameLabel")}
        value={[internalName || undefinedValue]}
        titleEnd={<EditButton onEdit={onEdit} field="internalName" />}
      />
      <PreferenceLine
        title={t("clientCompany.streetLabel")}
        value={[street]}
        titleEnd={<EditButton onEdit={onEdit} field="street" />}
      />
      <PreferenceLine
        title={t("clientCompany.postalCodeLabel")}
        value={[postalCode]}
        titleEnd={<EditButton onEdit={onEdit} field="postalCode" />}
      />
      <PreferenceLine
        title={t("clientCompany.cityLabel")}
        value={[city]}
        titleEnd={<EditButton onEdit={onEdit} field="city" />}
      />
      <PreferenceLine title={t("clientCompany.countryLabel")} value={[country || undefinedValue]} />
      <PreferenceLine
        title={t("clientCompany.emailLabel")}
        value={[email]}
        titleEnd={<EditButton onEdit={onEdit} field="email" />}
      />
      <PreferenceLine
        title={t("clientCompany.emailBisLabel")}
        value={[emailBis || undefinedValue]}
        titleEnd={<EditButton onEdit={onEdit} field="emailBis" />}
      />
      <PreferenceLine
        title={t("clientCompany.numberTVALabel")}
        value={[numberTVA || undefinedValue]}
        titleEnd={<EditButton onEdit={onEdit} field="numberTVA" />}
      />
      <PreferenceLine
        title={t("clientCompany.companySiretLabel")}
        value={[companySiret]}
        titleEnd={<EditButton onEdit={onEdit} field="companySiret" />}
      />
      <PreferenceLine
        title={t("clientCompany.companyEoriLabel")}
        value={[companyEori || undefinedValue]}
        titleEnd={<EditButton onEdit={onEdit} field="companyEori" />}
      />
      <PreferenceLine
        title={t("clientCompany.midCodeLabel")}
        value={[midCode || undefinedValue]}
        titleEnd={<EditButton onEdit={onEdit} field="midCode" />}
      />
    </div>
  );
};
